import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api, setPracticeIdProvider } from "@/lib/api";
import { type Practice } from "@/lib/types";

/** A switcher entry. `configured` entries are practices with a `practice` row in voice-service DB; `unconfigured`
 *  entries are Emmy facilities the user can access via `cognito:groups` but which don't have hlas-emmy set up yet
 *  (no practice row whose `emmyFacilityId` matches). PRD-010 Stage 2 §Phase 2.2 — the doctor should be able to
 *  switch to an unconfigured facility and see an explanation page rather than be silently locked out.
 */
export type PracticeOption =
  | { kind: "configured"; practice: Practice }
  | { kind: "unconfigured"; facilityId: string; name?: string };

interface PracticeContextValue {
  /** Configured-only practices (what `/api/practices` returned). Used by pages that only care about real practices. */
  practices: Practice[];
  /** Union of configured practices + unconfigured facilities, ordered with configured first then unconfigured. */
  options: PracticeOption[];
  activeOption: PracticeOption | null;
  setActiveOption: (opt: PracticeOption) => void;
  /** Convenience: the active option's practice if it's configured, else null. Equivalent to the old `activePractice`. */
  activePractice: Practice | null;
  isLoading: boolean;
  error: Error | null;
}

const PracticeContext = createContext<PracticeContextValue | null>(null);

// Storage key is prefixed with the option kind to disambiguate (practice id and facility UUID are both strings and
// could in principle collide).
const STORAGE_KEY = "hlasemmy.activeOption";

const serialise = (opt: PracticeOption): string =>
  opt.kind === "configured" ? `practice:${opt.practice.id}` : `facility:${opt.facilityId}`;

const parseStored = (
  raw: string | null,
  options: PracticeOption[],
): PracticeOption | null => {
  if (!raw) return null;
  const [kind, ...rest] = raw.split(":");
  const val = rest.join(":");
  if (kind === "practice") {
    return options.find((o) => o.kind === "configured" && o.practice.id === val) ?? null;
  }
  if (kind === "facility") {
    return options.find((o) => o.kind === "unconfigured" && o.facilityId === val) ?? null;
  }
  return null;
};

interface ProviderProps {
  /** Facilities the signed-in doctor has in `cognito:groups`. Drives the "unconfigured facilities" list. */
  accessibleFacilityIds: string[];
  children: ReactNode;
}

export const PracticeProvider = ({ accessibleFacilityIds, children }: ProviderProps) => {
  const queryClient = useQueryClient();

  const [practices, setPractices] = useState<Practice[]>([]);
  // Facility id → friendly name from Emmy's ListFacilities. Populated asynchronously; the combobox falls back to an
  // "Ordinace {uuid[:8]}" placeholder until this lands.
  const [facilityNames, setFacilityNames] = useState<Map<string, string>>(new Map());
  const [storedKey, setStoredKey] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // Fire both requests in parallel. `api.myFacilities()` can fail silently — missing names just degrade to the UUID
    // placeholder; the rest of the app still works. `listPractices` is the blocking one.
    const practicesP = api.listPractices();
    const facilitiesP = api
      .myFacilities()
      .catch((e) => {
        console.warn("Failed to load Emmy facility names:", e);
        return [] as { id: string; name: string }[];
      });

    Promise.all([practicesP, facilitiesP])
      .then(([list, facilities]) => {
        if (cancelled) return;
        setPractices(list);
        setFacilityNames(new Map(facilities.map((f) => [f.id, f.name])));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options: PracticeOption[] = useMemo(() => {
    const configuredFacilityIds = new Set(
      practices.map((p) => p.emmyFacilityId).filter((x): x is string => !!x),
    );
    const configured: PracticeOption[] = practices.map((p) => ({ kind: "configured", practice: p }));
    const unconfigured: PracticeOption[] = accessibleFacilityIds
      .filter((fid) => !configuredFacilityIds.has(fid))
      .sort()
      .map((facilityId) => ({
        kind: "unconfigured",
        facilityId,
        name: facilityNames.get(facilityId),
      }));
    return [...configured, ...unconfigured];
  }, [practices, accessibleFacilityIds, facilityNames]);

  const activeOption: PracticeOption | null = useMemo(() => {
    if (options.length === 0) return null;
    const fromStore = parseStored(storedKey, options);
    return fromStore ?? options[0];
  }, [storedKey, options]);

  // Wire X-Practice-Id only when the active option is a real configured practice. Unconfigured facilities have no
  // practice id to send, so we clear the header. The backend will 404/403 practice-scoped API calls anyway — the
  // FE should simply stay on the FacilityNotSetUpPage.
  useEffect(() => {
    const practiceId =
      activeOption?.kind === "configured" ? activeOption.practice.id : null;
    setPracticeIdProvider(() => practiceId);
  }, [activeOption]);

  const setActiveOption = useCallback(
    (opt: PracticeOption) => {
      const newKey = serialise(opt);
      if (newKey === storedKey) return;
      setStoredKey(newKey);
      try {
        localStorage.setItem(STORAGE_KEY, newKey);
      } catch {
        /* storage unavailable; best-effort */
      }
      queryClient.clear();
    },
    [storedKey, queryClient],
  );

  const activePractice: Practice | null =
    activeOption?.kind === "configured" ? activeOption.practice : null;

  return (
    <PracticeContext.Provider
      value={{ practices, options, activeOption, setActiveOption, activePractice, isLoading, error }}
    >
      {children}
    </PracticeContext.Provider>
  );
};

export const usePracticeContext = (): PracticeContextValue => {
  const ctx = useContext(PracticeContext);
  if (!ctx) {
    throw new Error("usePracticeContext must be used inside <PracticeProvider>");
  }
  return ctx;
};

/** Same as `usePracticeContext` but returns null when not inside a provider. Used by pages that render under
 *  BOTH AdminApp (no provider) and DoctorApp (provider mounted) — e.g. /calls — so they can pick up the
 *  doctor's active practice without crashing in admin mode. */
export const useOptionalPracticeContext = (): PracticeContextValue | null =>
  useContext(PracticeContext);
